import { createReducer, on } from '@ngrx/store';
import { IUploadingFilesState } from '../models/uploading-files.model';
import {
  changeUploadedImageRotate,
  changeUploadedImageRotateFailed,
  changeUploadedImageRotateSuccess,
  changeUploadingFilesInEditMode,
  changeUploadingFilesList,
  cleanCreatePhotoReportUploadingImages,
  clearUploadedFilesData,
  deleteUploadedFilesData,
  initUploadFile,
  initUploadFileFailed,
  initUploadFileSuccess,
  updateFileLocalUrl,
  updateUploadedFileData,
  uploadFileFailed,
  uploadFileSuccess,
} from '../actions/uploading-files.actions';
import { closePostModal, createNewPost, editPost } from '../actions/posts.actions';
import { createPhotoReportSuccess } from '../actions/photo-report.action';
import { IFileData, IUploadingFile } from '../../../shared/services/posts/posts.type';
import { FileType } from '../../../shared/enums';
import { environment } from '../../../../environments/environment';
import { imagePlaceholderUrl } from 'src/app/shared/constants';
import isImage from 'src/app/shared/utils/isImage';
import deepClone from 'src/app/shared/utils/deepClone';

const sortUploadingFilesByCategory = (
  uploadingFilesByCategory: Record<string, IUploadingFile[]> | null,
) => {
  if (!uploadingFilesByCategory) {
    return null;
  }

  const newObject: Record<string, IUploadingFile[]> = {};

  Object.keys(uploadingFilesByCategory).forEach((key) => {
    newObject[key] = deepClone(uploadingFilesByCategory[key]);
    newObject[key].sort((a, b) => a.localId - b.localId);
  });

  return newObject;
};

const appendFileToCategory = (
  uploadingFilesByCategory: Record<string, IUploadingFile[]> | null,
  imageCategory: string | undefined,
  file: IUploadingFile,
): Record<string, IUploadingFile[]> | null => {
  if (!imageCategory) {
    return uploadingFilesByCategory;
  }

  const existing = uploadingFilesByCategory?.[imageCategory] ?? [];
  if (existing.some((item) => item.localId === file.localId)) {
    return uploadingFilesByCategory;
  }

  return sortUploadingFilesByCategory({
    ...(uploadingFilesByCategory ?? {}),
    [imageCategory]: [...existing, file],
  });
};

const initialState: IUploadingFilesState = {
  uploadingFiles: [],
  uploadingFilesByCategory: null,
  uploadingFilesOfEditedPost: [],
  postEditMode: false,
  localId: 0,
  error: null,
  isLoading: false,
};

function getLocalRotateAngle(file: IUploadingFile) {
  if (file.localRotateAngle === 270) {
    return 0;
  } else if (!file.localRotateAngle) {
    return 90;
  } else {
    return file.localRotateAngle + 90;
  }
}

export const UploadingFilesReducer = createReducer(
  initialState,
  on(
    initUploadFileFailed,
    uploadFileFailed,
    (state: IUploadingFilesState, action): IUploadingFilesState => {
      const isUploadFailed = action.type === uploadFileFailed.type;
      const failedLocalId = isUploadFailed ? action.localId : undefined;
      const failedImageCategory = isUploadFailed ? action.imageCategory : undefined;

      return {
        ...state,
        error: action.error,
        isLoading: false,
        uploadingFiles:
          !state.postEditMode && isUploadFailed && state.uploadingFiles
            ? state.uploadingFiles.filter((file) => file.localId !== failedLocalId)
            : state.uploadingFiles,
        uploadingFilesOfEditedPost:
          state.postEditMode && isUploadFailed && state.uploadingFilesOfEditedPost
            ? state.uploadingFilesOfEditedPost.filter((file) => file.localId !== failedLocalId)
            : state.uploadingFilesOfEditedPost,
        uploadingFilesByCategory:
          isUploadFailed && failedImageCategory && state.uploadingFilesByCategory
            ? sortUploadingFilesByCategory({
                ...state.uploadingFilesByCategory,
                [failedImageCategory]: state.uploadingFilesByCategory[failedImageCategory].filter(
                  (file: IUploadingFile) => file.localId !== failedLocalId,
                ),
              })
            : state.uploadingFilesByCategory,
      };
    },
  ),
  on(
    initUploadFile,
    (state: IUploadingFilesState): IUploadingFilesState => ({
      ...state,
      error: null,
      isLoading: true,
    }),
  ),
  on(initUploadFileSuccess, (state: IUploadingFilesState, action): IUploadingFilesState => {
    const newFile: IUploadingFile = {
      localId: action.localId,
      localUrl: action.localUrl,
      localName: action.localName,
      localSize: action.localSize,
      fileType: action.fileType,
      imageCategory: action.imageCategory,
      localRotateAngle: 0,
      isLoading: true,
    };

    return {
      ...state,
      uploadingFiles: !state.postEditMode
        ? state.uploadingFiles.some((file) => file.localId === action.localId)
          ? state.uploadingFiles
          : [...state.uploadingFiles, newFile]
        : state.uploadingFiles,
      uploadingFilesOfEditedPost: state.postEditMode
        ? state.uploadingFilesOfEditedPost.some((file) => file.localId === action.localId)
          ? state.uploadingFilesOfEditedPost
          : [...state.uploadingFilesOfEditedPost, newFile]
        : state.uploadingFilesOfEditedPost,
      uploadingFilesByCategory: appendFileToCategory(
        state.uploadingFilesByCategory,
        action.imageCategory,
        newFile,
      ),
      localId: action.localId + 1,
      error: null,
    };
  }),
  on(
    uploadFileSuccess,
    (state: IUploadingFilesState, action): IUploadingFilesState => ({
      ...state,
      isLoading: false,
      uploadingFiles: !state.postEditMode
        ? state.uploadingFiles.map((file) =>
            file.localId === action.localId
              ? {
                  ...file,
                  isLoading: file.localUrl === imagePlaceholderUrl,
                  uploadedFileData: action.uploadedFileData,
                  id: action.id,
                }
              : file,
          )
        : state.uploadingFiles,
      uploadingFilesOfEditedPost: state.postEditMode
        ? state.uploadingFilesOfEditedPost.map((file) =>
            file.localId === action.localId
              ? {
                  ...file,
                  isLoading: file.localUrl === imagePlaceholderUrl,
                  uploadedFileData: action.uploadedFileData,
                  id: action.id,
                }
              : file,
          )
        : state.uploadingFiles,
      uploadingFilesByCategory: sortUploadingFilesByCategory(
        action.imageCategory && state.uploadingFilesByCategory
          ? {
              ...state.uploadingFilesByCategory,
              [action.imageCategory]: state.uploadingFilesByCategory[action.imageCategory].map(
                (file: IUploadingFile) =>
                  file.localId === action.localId
                    ? {
                        ...file,
                        isLoading: file.localUrl === imagePlaceholderUrl,
                        uploadedFileData: action.uploadedFileData,
                        id: action.id,
                      }
                    : file,
              ),
            }
          : null,
      ),
      error: null,
    }),
  ),
  on(
    updateFileLocalUrl,
    (state: IUploadingFilesState, action): IUploadingFilesState => ({
      ...state,
      uploadingFiles: !state.postEditMode
        ? state.uploadingFiles.map((file) =>
            file.localId === action.localId
              ? { ...file, isLoading: !file.uploadedFileData, localUrl: action.localUrl }
              : file,
          )
        : state.uploadingFiles,
      uploadingFilesOfEditedPost: state.postEditMode
        ? state.uploadingFilesOfEditedPost.map((file) =>
            file.localId === action.localId
              ? { ...file, isLoading: !file.uploadedFileData, localUrl: action.localUrl }
              : file,
          )
        : state.uploadingFiles,
      uploadingFilesByCategory: sortUploadingFilesByCategory(
        action.imageCategory && state.uploadingFilesByCategory
          ? {
              ...state.uploadingFilesByCategory,
              [action.imageCategory]: state.uploadingFilesByCategory[action.imageCategory].map(
                (file: IUploadingFile) =>
                  file.localId === action.localId
                    ? { ...file, isLoading: !file.uploadedFileData, localUrl: action.localUrl }
                    : file,
              ),
            }
          : null,
      ),
      error: null,
    }),
  ),
  on(
    updateUploadedFileData,
    (state: IUploadingFilesState, action): IUploadingFilesState => ({
      ...state,
      uploadingFiles: !state.postEditMode
        ? state.uploadingFiles.map((file) =>
            file.localId === action.localId
              ? { ...file, uploadedFileData: action.uploadedFileData }
              : file,
          )
        : state.uploadingFiles,
      uploadingFilesOfEditedPost: state.postEditMode
        ? state.uploadingFilesOfEditedPost.map((file) =>
            file.localId === action.localId
              ? { ...file, uploadedFileData: action.uploadedFileData }
              : file,
          )
        : state.uploadingFiles,
      uploadingFilesByCategory: sortUploadingFilesByCategory(
        action.imageCategory && state.uploadingFilesByCategory
          ? {
              ...state.uploadingFilesByCategory,
              [action.imageCategory]: state.uploadingFilesByCategory[action.imageCategory].map(
                (file: IUploadingFile) =>
                  file.localId === action.localId
                    ? { ...file, uploadedFileData: action.uploadedFileData }
                    : file,
              ),
            }
          : null,
      ),
    }),
  ),
  on(
    changeUploadingFilesInEditMode,
    (state: IUploadingFilesState, action): IUploadingFilesState => ({
      ...state,
      uploadingFilesByCategory: sortUploadingFilesByCategory(action.photoReportData),
    }),
  ),
  on(
    changeUploadingFilesList,
    (state: IUploadingFilesState, action): IUploadingFilesState => ({
      ...state,
      uploadingFilesOfEditedPost: action.uploadingFiles.files.map(
        (file: IFileData): IUploadingFile => ({
          isLoading: false,
          id: file.id,
          localRotateAngle: 0,
          localId: file.id,
          localUrl: environment.befreeUrl + file.path,
          localName: file.name,
          localSize: file.size,
          fileType: isImage(file) ? FileType.image : FileType.file,
        }),
      ),
      postEditMode: action.isEditMode,
    }),
  ),
  on(
    deleteUploadedFilesData,
    (state: IUploadingFilesState, action): IUploadingFilesState => ({
      ...state,
      uploadingFiles: !state.postEditMode
        ? state.uploadingFiles.filter((file) => file.localId !== action.localIdToBeDeleted)
        : state.uploadingFiles,
      uploadingFilesOfEditedPost: state.postEditMode
        ? state.uploadingFilesOfEditedPost.filter(
            (file) => file.localId !== action.localIdToBeDeleted,
          )
        : [],
      uploadingFilesByCategory: sortUploadingFilesByCategory(
        action.imageCategory && state.uploadingFilesByCategory
          ? {
              ...state.uploadingFilesByCategory,
              [action.imageCategory]: state.uploadingFilesByCategory[action.imageCategory].filter(
                (file: IUploadingFile) =>
                  action.imageId
                    ? file.id !== action.imageId
                    : file.localId !== action.localIdToBeDeleted,
              ),
            }
          : null,
      ),
    }),
  ),
  on(
    changeUploadedImageRotate,
    (state: IUploadingFilesState, action): IUploadingFilesState => ({
      ...state,
      uploadingFiles: state.uploadingFiles.map((file) =>
        file.localId === action.localId
          ? {
              ...file,
              isLoading: true,
            }
          : file,
      ),
      uploadingFilesByCategory: sortUploadingFilesByCategory(
        action.imageCategory && state.uploadingFilesByCategory
          ? {
              ...state.uploadingFilesByCategory,
              [action.imageCategory]: state.uploadingFilesByCategory[action.imageCategory].map(
                (file: IUploadingFile) =>
                  (action.id ? action.id === file.id : file.localId === action.localId)
                    ? {
                        ...file,
                        isLoading: true,
                      }
                    : file,
              ),
            }
          : null,
      ),
    }),
  ),
  on(
    changeUploadedImageRotateSuccess,
    (state: IUploadingFilesState, action): IUploadingFilesState => ({
      ...state,
      uploadingFiles: state.uploadingFiles.map((file) =>
        file.localId === action.localId
          ? {
              ...file,
              isLoading: false,
              localRotateAngle: getLocalRotateAngle(file),
            }
          : file,
      ),
      uploadingFilesByCategory: sortUploadingFilesByCategory(
        action.imageCategory && state.uploadingFilesByCategory
          ? {
              ...state.uploadingFilesByCategory,
              [action.imageCategory]: state.uploadingFilesByCategory[action.imageCategory].map(
                (file: IUploadingFile) => {
                  return (action.id ? action.id === file.id : file.localId === action.localId)
                    ? {
                        ...file,
                        isLoading: false,
                        localRotateAngle: action.rotateAngle,
                      }
                    : file;
                },
              ),
            }
          : null,
      ),
    }),
  ),
  on(
    changeUploadedImageRotateFailed,
    (state: IUploadingFilesState, action): IUploadingFilesState => ({
      ...state,
      uploadingFiles: state.uploadingFiles.map((file) =>
        file.localId === action.localId
          ? {
              ...file,
              isLoading: false,
            }
          : file,
      ),
      uploadingFilesByCategory: sortUploadingFilesByCategory(
        action.imageCategory && state.uploadingFilesByCategory
          ? {
              ...state.uploadingFilesByCategory,
              [action.imageCategory]: state.uploadingFilesByCategory[action.imageCategory].map(
                (file: IUploadingFile) =>
                  file.localId === action.localId
                    ? {
                        ...file,
                        isLoading: false,
                      }
                    : file,
              ),
            }
          : null,
      ),
      error: action.error,
    }),
  ),
  on(
    closePostModal,
    createNewPost,
    editPost,
    createPhotoReportSuccess,
    cleanCreatePhotoReportUploadingImages,
    clearUploadedFilesData,
    (state: IUploadingFilesState): IUploadingFilesState => ({
      ...state,
      uploadingFiles: initialState.uploadingFiles,
      uploadingFilesOfEditedPost: [],
      uploadingFilesByCategory: null,
      postEditMode: false,
    }),
  ),
);
